import React, { type ChangeEvent, useEffect, useState } from 'react';
import { ReactComponent as ChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import { Input as RoccoInput } from '@rocco/components/input';
import { useControllableValue, useDebounceFn } from 'ahooks';
import { ReactComponent as SearchIcon } from '@rocco/icons/svg/search.svg';
import type { FilterConfigBarItemSearchable } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { isSearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { ScrollArea as RoccoScrollArea } from '@rocco/components/scroll-area';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';

interface FilterSearchableListProps extends React.PropsWithChildren {
    isLoading: boolean;
    searchInputPlaceholder: string;
    searchQuery?: string;
    onSearchQueryChange?: (searchQuery: string) => void;
}

export const FiltersBarSearchableList = (
    props: FilterBarElementProps<
        FilterConfigBarItemSearchable,
        SearchableListFilterState
    >,
) => {
    const { filterConfigItem, filterState, onFilterStateUpdate } = props;
    const { searchFilters } = useFiltersContext();
    const { handleAggregate } = searchFilters;

    const [isLoading, setIsLoading] = useState(false);
    const [showScrollDownIcon, setShowScrollDownIcon] = useState(
        filterState.hasMoreItems,
    );

    const filterKey = filterConfigItem.filterKey;

    const { run: searchAggregateRun } = useDebounceFn(
        (searchQuery: string) => {
            handleAggregate({
                aggregateType: 'searchListSearch',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        search: searchQuery,
                        after: filterState.after ?? undefined, // Search service wants `undefined`, not null
                        size: filterState.size,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: data => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        const newItems = newListState?.items ?? [];

                        onFilterStateUpdate(
                            {
                                ...filterState,
                                items: newItems,
                                after: newListState?.after ?? null,
                                hasMoreItems: !!newListState?.items.length,
                            },
                            false,
                        );
                    }
                },
            });
        },
        { wait: 500 },
    );

    const { run: loadMoreItems } = useDebounceFn(
        () => {
            if (!filterState.hasMoreItems) return;

            handleAggregate({
                aggregateType: 'searchListLoadMore',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        search: filterState.search,
                        after: filterState.after ?? undefined, // Search service wants `undefined`, not null
                        size: filterState.size,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: data => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        const previousSize = filterState.items.length;

                        const newItems = newListState?.items.length
                            ? [
                                  ...filterState.items,
                                  ...newListState.items.filter(
                                      item =>
                                          !filterState.items.some(
                                              i => i.value === item.value,
                                          ),
                                  ),
                              ]
                            : filterState.items;

                        setShowScrollDownIcon(previousSize !== newItems.length);

                        onFilterStateUpdate(
                            {
                                ...filterState,
                                items: newItems,
                                after: newListState?.after ?? null,
                                hasMoreItems: !!newListState?.items.length,
                            },
                            false,
                        );
                    }
                },
            });
        },
        { wait: 1000, leading: true },
    );

    const scrollAreaItemHeight = 2.5;
    const scrollAreaHeight = filterState.items.length * scrollAreaItemHeight;
    const scrollAreaOverflown = scrollAreaHeight > 21;
    const scrollAreaStyles = {
        height: `${scrollAreaOverflown ? 21 : scrollAreaHeight}rem`,
    };

    useEffect(() => {
        setShowScrollDownIcon(filterState.hasMoreItems);
    }, [filterState.hasMoreItems]);

    return (
        <FilterSearchableList
            isLoading={isLoading}
            searchInputPlaceholder={filterConfigItem.searchInputPlaceholder}
            searchQuery={filterState.search}
            onSearchQueryChange={updatedSearchQuery => {
                onFilterStateUpdate(
                    { ...filterState, search: updatedSearchQuery },
                    false,
                );
                searchAggregateRun(updatedSearchQuery);
            }}
        >
            <div>
                <RoccoScrollArea
                    style={scrollAreaStyles}
                    onCustomScroll={scroll => {
                        scroll.bottom < 10 && loadMoreItems();
                    }}
                    scrollBarVisibility="hidden"
                >
                    <div className="flex flex-col gap-2 items-stretch">
                        {filterState.items.map(item => (
                            <FiltersBarSearchableListItem
                                key={item.value}
                                item={item}
                                filterState={filterState}
                                onFilterStateUpdate={onFilterStateUpdate}
                            />
                        ))}
                    </div>
                </RoccoScrollArea>
                <div className="flex w-full justify-center relative h-2">
                    {scrollAreaOverflown && showScrollDownIcon && (
                        <ChevronDownIcon className="size-4.5 absolute -bottom-4" />
                    )}
                </div>
            </div>
        </FilterSearchableList>
    );
};

const FiltersBarSearchableListItem = ({
    item,
    filterState,
    onFilterStateUpdate,
}: {
    item: SingleValueFilterState;
    filterState: SearchableListFilterState;
} & Pick<
    FilterBarElementProps<
        FilterConfigBarItemSearchable,
        SearchableListFilterState
    >,
    'onFilterStateUpdate'
>) => {
    const {
        searchFilters: { handleToggleSearchableList },
    } = useFiltersContext();

    return (
        <div
            key={item.value}
            className="px-2 flex items-center h-8 cursor-pointer w-full
            hover:bg-primary-foreground hover:text-primary hover:border-black group rounded-2"
        >
            <RoccoCheckbox
                id={item.value}
                checked={item.checked}
                groupHover="light"
                onCheckedChange={checked => {
                    handleToggleSearchableList &&
                        onFilterStateUpdate(
                            handleToggleSearchableList(
                                filterState,
                                item,
                                !!checked,
                            ),
                            true,
                        );
                }}
            />
            <RoccoLabel htmlFor={item.value} target="checkbox">
                {item.label}
            </RoccoLabel>
        </div>
    );
};

const FilterSearchableList = (props: FilterSearchableListProps) => {
    const { children, isLoading, searchInputPlaceholder } = props;

    const [state, setState] = useControllableValue<string>(props, {
        defaultValue: '',
        valuePropName: 'searchQuery',
        trigger: 'onSearchQueryChange',
    });

    return (
        <div className={isLoading ? 'opacity-50' : undefined}>
            <RoccoInput
                placeholder={searchInputPlaceholder}
                value={state}
                color="light"
                variant="medium"
                shape="rounded"
                onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                    setState(evt.target.value);
                }}
                showClearButton={true}
                postfix={<SearchIcon className="size-5 stroke-gray-300" />}
            />
            <div className="mt-2 text-wrap">{children}</div>
        </div>
    );
};
